import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const consultantMeta: InsightMeta = {
  slug: "iso-consultant-vs-certification-body-in-iraq",
  metaTitle: {
    en: "ISO Consultant vs Certification Body in Iraq | Who Does What",
    ar: "الفرق بين استشاري الأيزو والجهة المانحة للشهادات في العراق",
  },
  title: {
    en: "ISO Consultant vs Certification Body in Iraq: Who Does What",
    ar: "استشاري الأيزو والجهة المانحة للشهادات في العراق: من يبني النظام ومن يمنح الشهادة",
  },
  description: {
    en: "An ISO consultant builds your management system. An accredited certification body audits it and issues the certificate. Here is who does what in Iraq.",
    ar: "استشاري الأيزو يبني نظام الإدارة لديك، والجهة المانحة للشهادات تدققه باستقلالية وتصدر الشهادة. اعرف دور كل طرف في العراق ولماذا يبقى الدوران منفصلين.",
  },
  datePublished: "2026-07-29",
  dateModified: "2026-10-09",
  dateLabel: { en: "9 October 2026", ar: "9 تشرين الأول 2026" },
  faq: [
    {
      q: {
        en: "Do I need a consultant to get ISO certified in Iraq?",
        ar: "هل أحتاج إلى استشاري للحصول على شهادة الأيزو في العراق؟",
      },
      a: {
        en: "A consultant is not a certification requirement. A company can prepare its own system if it has the necessary competence and resources. The decision is whether preparation support is useful for its scope and readiness; hiring a consultant does not guarantee certification.",
        ar: "الاستعانة باستشاري ليست شرطاً لمنح الشهادة. تستطيع الشركة إعداد نظامها إذا توفرت لديها الكفاءة والموارد اللازمة. يتوقف القرار على فائدة دعم التحضير لنطاقها وجاهزيتها؛ ولا يضمن التعاقد مع استشاري الحصول على الشهادة.",
      },
    },
    {
      q: {
        en: "Can the same company consult and certify?",
        ar: "هل يمكن للشركة نفسها أن تقدم الاستشارة وتمنح الشهادة؟",
      },
      a: {
        en: "Preparation and certification must preserve impartiality. Identify the legal entities and ask the certification body how it manages consultancy relationships and conflicts of interest. A consultant cannot make the independent certification decision.",
        ar: "يجب أن يحافظ التحضير ومنح الشهادة على الحيادية. حدّد الكيانات القانونية، واسأل الجهة المانحة عن إدارة العلاقات الاستشارية وتعارض المصالح. لا يتخذ الاستشاري القرار المستقل بمنح الشهادة.",
      },
    },
    {
      q: { en: "What does IraqCert actually do?", ar: "ما الذي تقوم به عراق سيرت فعليا؟" },
      a: {
        en: "IraqCert supports gap analysis, documentation, training and audit coordination within the agreed scope. The certification body performs the certification audit and makes its decision. Confirm the proposed issuer and accreditation scope for the requested standard, including the separate route needed for ISO 29001.",
        ar: "تدعم عراق سيرت تحليل الفجوات وإعداد الوثائق والتدريب وتنسيق التدقيق ضمن النطاق المتفق عليه. تنفذ الجهة المانحة تدقيق الشهادة وتتخذ قرارها. أكّد الجهة المقترحة ونطاق اعتمادها للمواصفة المطلوبة، بما في ذلك المسار المستقل اللازم لـ ISO 29001.",
      },
    },
    {
      q: {
        en: "How do I choose an ISO consultant in Iraq?",
        ar: "كيف أختار استشاري ايزو في العراق؟",
      },
      a: {
        en: "Check three things before you sign: which accredited certification body will issue the certificate, whether that certificate will be verifiable on IAF CertSearch, and whether the consultant and the certification body are separate organizations. Ask for references in your sector. Walk away from anyone who promises a certificate without a certification audit.",
        ar: "تحقق من ثلاثة أمور قبل التوقيع: أي جهة مانحة معتمدة ستصدر الشهادة، وهل يمكن التحقق من الشهادة عبر منصة IAF CertSearch، وهل الاستشاري والجهة المانحة جهتان منفصلتان. اطلب مراجع عمل من قطاعك نفسه. وابتعد عن أي جهة تعدك بشهادة من دون تدقيق منح الشهادة.",
      },
    },
    {
      q: { en: "Who issues the certificate at the end?", ar: "من يصدر الشهادة في النهاية؟" },
      a: {
        en: "The certification body makes the decision and issues the certificate. AQSR is IraqCert’s partner within its disclosed UAF-accredited scope; ISO 29001 is outside that scope. Confirm the relevant issuer, surveillance programme and verification route before agreeing the work.",
        ar: "تتخذ جهة منح الشهادات القرار وتصدر الشهادة. AQSR هي شريك عراق سيرت ضمن نطاق اعتماد UAF المعلن لها، وISO 29001 خارج هذا النطاق. أكّد الجهة المعنية وبرنامج المراقبة ومسار التحقق قبل الاتفاق على العمل.",
      },
    },
  ],
};

const tableRows = [
  {
    c1: { en: "Gap analysis", ar: "تحليل الفجوات" },
    c2: { en: "Runs it and maps what your system lacks", ar: "ينفذه ويحدد ما ينقص نظامك" },
    c3: { en: "No role", ar: "لا دور لها فيه" },
  },
  {
    c1: { en: "Documentation", ar: "إعداد الوثائق" },
    c2: { en: "Writes it with your team", ar: "يكتبها مع فريقك" },
    c3: { en: "Reviews it in the Stage 1 audit", ar: "تراجعها في المرحلة الأولى من التدقيق" },
  },
  {
    c1: { en: "Internal audit", ar: "التدقيق الداخلي" },
    c2: { en: "Runs it or trains your team to run it", ar: "ينفذه أو يدرب فريقك على تنفيذه" },
    c3: { en: "Verifies its records during the audit", ar: "تتحقق من سجلاته أثناء التدقيق" },
  },
  {
    c1: { en: "Certification audit", ar: "تدقيق منح الشهادة" },
    c2: { en: "Prepares you for it, cannot conduct it", ar: "يجهزك له ولا يجوز أن ينفذه" },
    c3: { en: "Conducts Stage 1 and Stage 2", ar: "تنفذ المرحلتين الأولى والثانية" },
  },
  {
    c1: { en: "Issuing the certificate", ar: "إصدار الشهادة" },
    c2: { en: "Never issues it", ar: "لا يصدرها أبدا" },
    c3: { en: "Makes the decision and issues it", ar: "تتخذ قرار المنح وتصدر الشهادة" },
  },
];

export function ConsultantBody() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "An ISO consultant helps your team prepare a management system. A certification body independently assesses it and decides whether to issue a certificate. ISO/IEC 17021-1 addresses the competence, consistency and impartiality of certification bodies. Ask who will provide preparation, who will audit, and how the certification body manages conflicts of interest.",
          ar: "يساعد استشاري الأيزو فريقك في إعداد نظام الإدارة. وتقيّمه جهة منح الشهادات بصورة مستقلة، وتقرر إصدار الشهادة من عدمه. تتناول ISO/IEC 17021-1 كفاءة الجهات المانحة واتساق عملها وحياديتها. اسأل من سيتولى التحضير والتدقيق، وكيف تدير الجهة المانحة تعارض المصالح.",
        })}{" "}
        {t({ en: "Start with our guide to ", ar: "ابدأ من دليلنا عن " })}
        <LangLink
          to="/insights/trusted-iso-certification-body-in-iraq"
          className="font-semibold text-primary hover:underline"
        >
          {t({
            en: "how to choose a trusted ISO certification body in Iraq",
            ar: "كيفية اختيار جهة موثوقة لشهادات الأيزو في العراق",
          })}
        </LangLink>
        {t({ en: ", or see ", ar: "، أو اطّلع على " })}
        <LangLink to="/services" className="font-semibold text-primary hover:underline">
          {t({ en: "ISO certification services", ar: "خدمات شهادات الأيزو" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>
      <P>
        {t({
          en: "IraqCert works on preparation and coordination. The certification body owns the assessment and decision, and its accreditation must be checked for the proposed standard and activity. Our About page discloses AQSR’s scope; do not assume it includes ISO 29001.",
          ar: "تعمل عراق سيرت في التحضير والتنسيق. وتتولى الجهة المانحة التقييم والقرار، ويجب مراجعة اعتمادها للمواصفة والنشاط المقترحين. توضح صفحة «من نحن» نطاق AQSR؛ فلا تفترض أنه يشمل ISO 29001.",
        })}
      </P>

      <H2>{t({ en: "What an ISO Consultant Does", ar: "ماذا يفعل استشاري الأيزو" })}</H2>
      <P>
        {t({
          en: "An ISO consultant builds and prepares your management system so it can pass an independent audit. The work starts with a gap analysis that compares your current operation against the standard. The consultant then writes the documentation with your team: policies, procedures, and records. Staff get trained on what those documents require of them.",
          ar: "استشاري الأيزو يبني نظام الإدارة في شركتك ويجهزه لاجتياز تدقيق مستقل. يبدأ العمل بتحليل الفجوات الذي يقارن وضعك الحالي بمتطلبات المواصفة. ثم يعد الاستشاري الوثائق مع فريقك: السياسات والإجراءات والسجلات. ويدرب الموظفين على ما تتطلبه هذه الوثائق منهم.",
        })}
      </P>
      <P>
        {t({
          en: "Before the certification audit, the consultant can support internal-audit preparation and organize evidence for management review. Your management team remains responsible for the system, its review and corrective actions. Agree impartial arrangements for internal auditing rather than assuming the author of every process should assess their own work.",
          ar: "قبل تدقيق الشهادة، يستطيع الاستشاري دعم التحضير للتدقيق الداخلي وتنظيم أدلة مراجعة الإدارة. وتبقى إدارة شركتك مسؤولة عن النظام ومراجعته والإجراءات التصحيحية. اتفق على ترتيبات حيادية للتدقيق الداخلي، بدلاً من افتراض أن واضع كل إجراء ينبغي أن يقيّم عمله بنفسه.",
        })}
      </P>
      <H2>{t({ en: "What a Certification Body Does", ar: "ماذا تفعل الجهة المانحة للشهادات" })}</H2>
      <P>
        {t({
          en: "A certification body independently audits your management system against the standard and issues the certificate when it conforms. The audit runs in two stages: Stage 1 reviews your documentation and readiness, Stage 2 audits how the system works in practice. The auditor records major and minor nonconformities and requires correction before the certification decision.",
          ar: "الجهة المانحة للشهادات تدقق نظام إدارتك باستقلالية مقابل متطلبات المواصفة وتصدر الشهادة عند ثبوت المطابقة. يجري التدقيق على مرحلتين: المرحلة الأولى تراجع الوثائق والجاهزية، والمرحلة الثانية تدقق التطبيق الفعلي في مواقع العمل. يسجل المدقق حالات عدم المطابقة الكبرى والصغرى ويشترط تصحيحها قبل قرار منح الشهادة.",
        })}
      </P>
      <P>
        {t({
          en: "The issuing body defines the certification programme, surveillance and recertification requirements. Accreditation supports confidence in the body’s competence within its scope. Buyer acceptance still depends on the standard, activities, sites and any additional written conditions.",
          ar: "تحدد الجهة المانحة برنامج الشهادة ومتطلبات المراقبة وإعادة المنح. يدعم الاعتماد الثقة بكفاءة الجهة ضمن نطاقها. ويبقى قبول المشتري مرتبطاً بالمواصفة والأنشطة والمواقع وأي شروط مكتوبة إضافية.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th className={thClass}>{t({ en: "Task", ar: "المهمة" })}</th>
            <th className={thClass}>{t({ en: "Consultant", ar: "الاستشاري" })}</th>
            <th className={thClass}>
              {t({ en: "Certification body", ar: "الجهة المانحة للشهادات" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row) => (
            <tr key={row.c1.en} className="border-t border-border">
              <td className={`${tdClass} font-semibold text-foreground`}>{t(row.c1)}</td>
              <td className={tdClass}>{t(row.c2)}</td>
              <td className={tdClass}>{t(row.c3)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <H2>
        {t({
          en: "Why the Same Body Cannot Consult and Certify",
          ar: "لماذا لا تجمع جهة واحدة بين الاستشارة ومنح الشهادة",
        })}
      </H2>
      <P>
        {t({
          en: "The certification body must safeguard impartiality when assessing a system prepared with consultancy support. Ask it to explain the applicable restrictions on consultancy, related entities and auditor assignments. Separate company names alone are not proof that every impartiality risk has been addressed.",
          ar: "يجب أن تحافظ الجهة المانحة على الحيادية عند تقييم نظام أُعدّ بدعم استشاري. اطلب منها توضيح القيود المنطبقة على الاستشارة والجهات المرتبطة وتكليف المدققين. اختلاف أسماء الشركات وحده لا يثبت معالجة كل مخاطر الحيادية.",
        })}
      </P>
      <P>
        {t({
          en: "If an offer combines preparation and certification, request the names and responsibilities of the parties and confirmation from the certification body of its impartiality arrangements. Do not accept a promised certificate as a substitute for an independent assessment.",
          ar: "إذا جمع العرض التحضير ومنح الشهادة، فاطلب أسماء الأطراف ومسؤولياتها وتأكيداً من الجهة المانحة بشأن ترتيبات الحيادية. ولا تقبل وعداً بالشهادة بديلاً عن تقييم مستقل.",
        })}
      </P>
      <H2>{t({ en: "Where IraqCert Fits", ar: "أين يقع دور عراق سيرت" })}</H2>
      <P>
        {t({
          en: "IraqCert provides preparation support. AQSR is the partner certification body for the standards within its disclosed UAF-accredited scope. For ISO 29001, the proposed issuing body and its accreditation scope must be confirmed separately before an audit is booked.",
          ar: "تقدم عراق سيرت دعم التحضير. AQSR هي الجهة المانحة الشريكة للمواصفات المشمولة بنطاق اعتماد UAF المعلن لها. أما ISO 29001 فتستلزم تأكيد الجهة المانحة المقترحة ونطاق اعتمادها بصورة مستقلة قبل حجز التدقيق.",
        })}
      </P>
      <P>
        {t({
          en: "Agree the support tasks and audit coordination in writing. The certification body performs its independent assessment and decision; the buyer separately checks whether the certificate meets its procurement requirement. Neither preparation support nor accreditation guarantees a contract.",
          ar: "اتفق كتابةً على مهام الدعم وتنسيق التدقيق. تنفذ الجهة المانحة تقييمها المستقل وتتخذ القرار، بينما يتحقق المشتري بصورة منفصلة من مطابقة الشهادة لمتطلب الشراء. لا يضمن دعم التحضير أو الاعتماد الحصول على عقد.",
        })}
      </P>

      <H2>{t({ en: "Sources", ar: "المصادر" })}</H2>
      <P>
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "ISO: certification and accreditation", ar: "ISO: منح الشهادات والاعتماد" })}
        </a>
      </P>
      <P>
        <a
          href="https://www.iso.org/standard/61651.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "ISO: competence and impartiality of certification bodies",
            ar: "ISO: كفاءة جهات منح الشهادات وحياديتها",
          })}
        </a>
      </P>
    </>
  );
}
